import { Controller } from "@hotwired/stimulus"

// Shows only the fields relevant to the selected placement status, progressively
// as the placement moves through its lifecycle:
//   pending  -> status + original position only (no dates)
//   placed   -> + placed date + expected end date (auto-filled placed + 28, editable)
//   expired  -> + placed date + end date + final streams
//   removed  -> same as expired
export default class extends Controller {
  static targets = ["status", "placedDateGroup", "expectedEndGroup", "endGroup", "placedDate", "expectedEnd",
    "transition", "acceptGroup", "reasonGroup", "feeToggle", "feeFields",
    "packageToggle", "packageFields", "packageMonths", "projectedEnd"]
  // Package deals (2026-09-09): the row's term in months drives the
  // expected-end auto-fill (28 days for a default term, calendar months
  // for a package); the renew panel's checkbox reveals the months select.
  static values = { termMonths: { type: Number, default: 1 } }

  connect() {
    this.update()
  }

  update() {
    // Pitch-resolution panel: accept reveals the payment fields, decline/no-response
    // reveal the optional reason.
    if (this.hasTransitionTarget) {
      const t = this.transitionTarget.value
      this.toggle(this.hasAcceptGroupTarget && this.acceptGroupTarget, t === "accepted")
      this.toggle(this.hasReasonGroupTarget && this.reasonGroupTarget, t === "declined" || t === "no_response")
      const feeOn = this.hasFeeToggleTarget && this.feeToggleTarget.checked
      this.toggle(this.hasFeeFieldsTarget && this.feeFieldsTarget, feeOn)
      return
    }

    const status = this.hasStatusTarget ? this.statusTarget.value : "pending"
    const showPlacedDate = ["placed", "expired", "removed_early"].includes(status)
    const showExpected = status === "placed"
    const showEnd = ["expired", "removed_early"].includes(status)

    this.toggle(this.hasPlacedDateGroupTarget && this.placedDateGroupTarget, showPlacedDate)
    this.toggle(this.hasExpectedEndGroupTarget && this.expectedEndGroupTarget, showExpected)
    this.endGroupTargets.forEach(el => this.toggle(el, showEnd))

    if (status === "placed") this.autofillExpectedEnd()
  }

  toggle(el, show) {
    if (el) el.style.display = show ? "block" : "none"
  }

  // Expected end is the 4-week assumption from the placed date; fill it so the
  // field never shows an empty mm/dd/yyyy box, but leave it editable.
  autofillExpectedEnd() {
    if (!this.hasExpectedEndTarget) return
    let placed = this.hasPlacedDateTarget ? this.placedDateTarget.value : ""
    if (!placed) {
      placed = new Date().toISOString().slice(0, 10)
      if (this.hasPlacedDateTarget) this.placedDateTarget.value = placed
    }
    if (!this.expectedEndTarget.value) {
      this.expectedEndTarget.value = this.plus28(placed)
    }
  }

  placedDateChanged() {
    if (this.statusTarget.value === "placed" && this.hasExpectedEndTarget &&
        this.hasPlacedDateTarget && this.placedDateTarget.value) {
      this.expectedEndTarget.value = this.plus28(this.placedDateTarget.value)
    }
  }

  plus28(isoDate) {
    return this.plusTerm(isoDate, this.termMonthsValue)
  }

  plusTerm(isoDate, months) {
    const d = new Date(isoDate + "T00:00:00")
    if (months > 1) {
      const day = d.getDate()
      d.setMonth(d.getMonth() + months)
      if (d.getDate() !== day) d.setDate(0) // Jan 31 + 1 month = Feb 28
    } else {
      d.setDate(d.getDate() + 28)
    }
    const pad = n => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }

  togglePackage() {
    const on = this.packageToggleTarget.checked
    this.packageFieldsTarget.style.display = on ? "" : "none"
    this.packageMonthsTarget.disabled = !on
    this.packageChanged()
  }

  packageChanged() {
    if (!this.hasProjectedEndTarget) return
    const base = this.projectedEndTarget.dataset.baseDate
    if (!base) return
    const months = this.packageToggleTarget.checked ? parseInt(this.packageMonthsTarget.value, 10) : 1
    const iso = this.plusTerm(base, months)
    const d = new Date(iso + "T00:00:00")
    this.projectedEndTarget.textContent = "~" + d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  close() {
    const row = this.element.closest(".status-panel-row, .renew-panel-row")
    if (row) row.style.display = "none"
  }
}
